import { useEffect, useState } from "react";
import { Card, ErrorBanner, Pill, fmt } from "../components/ui";
import { api, type SendConfig, type User } from "../lib/api";
import { SendingSpeed } from "./SendingSpeed";

const ROLES = [
  { key: "admin", what: "Everything, including adding people" },
  { key: "manager", what: "Send blasts, edit campaigns, change limits" },
  { key: "specialist", what: "See contacts and the queue. Cannot send" },
  { key: "analyst", what: "Read-only" },
  { key: "autopilot", what: "Read-only, and runs the Autopilot. Cannot send" },
];

const DAYS = [
  { iso: 1, label: "Mon" },
  { iso: 2, label: "Tue" },
  { iso: 3, label: "Wed" },
  { iso: 4, label: "Thu" },
  { iso: 5, label: "Fri" },
  { iso: 6, label: "Sat" },
  { iso: 7, label: "Sun" },
];

/** "Mon-Sat" / "every day" / "Mon, Wed, Sun" - the same words the API uses,
 *  so the screen and the audit log never describe a change differently. */
function describeDays(days: number[] | null | undefined): string {
  if (!days || days.length === 0 || days.length === 7) return "every day";
  const sorted = [...days].sort((a, b) => a - b);
  const name = (d: number) => DAYS.find((x) => x.iso === d)?.label ?? String(d);
  const runs: [number, number][] = [];
  let start = sorted[0];
  let prev = sorted[0];
  for (const d of sorted.slice(1)) {
    if (d === prev + 1) { prev = d; continue; }
    runs.push([start, prev]);
    start = prev = d;
  }
  runs.push([start, prev]);
  return runs.map(([a, b]) => (a === b ? name(a) : `${name(a)}-${name(b)}`)).join(", ");
}

export function Settings({ me }: { me: { id: number; role: string } }) {
  const [users, setUsers] = useState<User[]>([]);
  const [cfg, setCfg] = useState<SendConfig | null>(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("specialist");
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [savingDays, setSavingDays] = useState(false);

  const load = () =>
    Promise.all([api.users(), api.sendConfig()])
      .then(([u, c]) => {
        setUsers(u);
        setCfg(c);
      })
      .catch((e) => setError(e.message));

  useEffect(() => {
    load();
  }, []);

  const add = () => {
    setError(null);
    api
      .addUser({ email, role })
      .then(() => {
        setNote(`${email} can now sign in with their Google account.`);
        setEmail("");
        load();
      })
      .catch((e) => setError(e.message));
  };

  const isAdmin = me.role === "admin";

  return (
    <>
      <ErrorBanner error={error} />
      {note && <div className="banner warn">{note}</div>}

      <h2 style={{ fontSize: 19 }}>Settings</h2>

      <Card
        title="Who can use this"
        footer={
          <span className="dim">
            Any Google account works. Adding an email here is the entire process — nothing
            changes in Google. Removing someone takes effect on their next click.
          </span>
        }
      >
        {isAdmin && (
          <div className="row w" style={{ marginBottom: 14 }}>
            <input
              className="f"
              style={{ width: 260 }}
              placeholder="someone@anywhere.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <select className="f" value={role} onChange={(e) => setRole(e.target.value)}>
              {ROLES.map((r) => (
                <option key={r.key} value={r.key}>
                  {r.key}
                </option>
              ))}
            </select>
            <button className="btn p" disabled={!email.includes("@")} onClick={add}>
              Give access
            </button>
            <span className="dim" style={{ fontSize: 12 }}>
              {ROLES.find((r) => r.key === role)?.what}
            </span>
          </div>
        )}

        <div className="tw">
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Last signed in</th>
                <th>Status</th>
                {isAdmin && <th></th>}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} style={{ opacity: u.active ? 1 : 0.5 }}>
                  <td style={{ fontWeight: 600 }}>{u.email}</td>
                  <td>
                    {isAdmin && u.id !== me.id ? (
                      <select
                        className="f"
                        value={u.role}
                        onChange={(e) =>
                          api.updateUser(u.id, { role: e.target.value }).then(load).catch((x) => setError(x.message))
                        }
                      >
                        {ROLES.map((r) => (
                          <option key={r.key} value={r.key}>
                            {r.key}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Pill tone="nu">{u.role}</Pill>
                    )}
                  </td>
                  <td className={u.last_login_at ? "" : "dim"}>{fmt.when(u.last_login_at)}</td>
                  <td>
                    <Pill tone={u.active ? "ok" : "ba"}>{u.active ? "active" : "removed"}</Pill>
                  </td>
                  {isAdmin && (
                    <td>
                      {u.id !== me.id && (
                        <button
                          className="btn s"
                          onClick={() =>
                            api.updateUser(u.id, { active: !u.active }).then(load).catch((x) => setError(x.message))
                          }
                        >
                          {u.active ? "Remove access" : "Restore"}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <SendingSpeed canChange={me.role === "admin" || me.role === "manager"} onError={setError} />

      {cfg && (
        <Card
          title="Overnight and weekend sign-ups"
          footer={
            <span className="dim">
              People who sign up outside sending hours - after 8 PM, before 11 AM, or on a Sunday - get their first
              text when sending opens. After a
              weekend that is hundreds of fresh leads, and they call back fast - so their first texts go out at
              most this many per 15 minutes from 11:00, earliest sign-up first. Daytime sign-ups are never held.
              0 means no limit.
            </span>
          }
        >
          <div className="row w" style={{ gap: 8 }}>
            <span style={{ fontSize: 12.5 }}>First texts, at most</span>
            <input
              className="f"
              type="number"
              min={0}
              max={300}
              style={{ width: 90 }}
              disabled={!(me.role === "admin" || me.role === "manager")}
              defaultValue={cfg.backlog_per_15_min}
              key={cfg.backlog_per_15_min}
              onBlur={(e) => {
                const n = Number(e.target.value);
                if (n === cfg.backlog_per_15_min || Number.isNaN(n)) return;
                api
                  .updateSendConfig({ backlog_per_15_min: n })
                  .then(load)
                  .catch((x) => setError(x.message));
              }}
            />
            <span style={{ fontSize: 12.5 }}>
              per 15 minutes
              {cfg.backlog_per_15_min > 0 && (
                <span className="dim"> · {cfg.backlog_per_15_min * 4} an hour</span>
              )}
            </span>
          </div>
        </Card>
      )}

      {cfg && (
        <Card
          title="Sending days"
          footer={
            <span className="dim">
              The one place this is decided. It beats every campaign's own rule, so turning a day on
              turns it on everywhere, at once, with no deploy. Sunday is normally off - tick it when
              you want an exception, untick it afterwards. Takes effect within about twenty seconds.
            </span>
          }
        >
          <div className="row w" style={{ gap: 6, flexWrap: "wrap" }}>
            {DAYS.map(({ iso, label }) => {
              const days = cfg.sending_weekdays ?? [1, 2, 3, 4, 5, 6, 7];
              const on = days.includes(iso);
              const canEdit = me.role === "admin" || me.role === "manager";
              return (
                <button
                  key={iso}
                  className={on ? "btn on" : "btn"}
                  disabled={!canEdit || savingDays}
                  aria-pressed={on}
                  title={on ? `Sending on ${label}` : `Not sending on ${label}`}
                  style={{
                    minWidth: 58,
                    fontWeight: on ? 600 : 400,
                    opacity: on ? 1 : 0.55,
                  }}
                  onClick={() => {
                    const next = on ? days.filter((d) => d !== iso) : [...days, iso].sort();
                    if (next.length === 0) {
                      setError("Pick at least one day.");
                      return;
                    }
                    setSavingDays(true);
                    api
                      .updateSendConfig({ sending_weekdays: next })
                      .then(load)
                      .catch((x) => setError(x.message))
                      .finally(() => setSavingDays(false));
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <div className="dim" style={{ marginTop: 8, fontSize: 12.5 }}>
            Sending on <b>{describeDays(cfg.sending_weekdays)}</b>
            {(cfg.sending_weekdays ?? []).includes(7) && (
              <span style={{ color: "var(--warn, #b45309)" }}>
                {" "}· Sunday is open. Remember to turn it off again.
              </span>
            )}
          </div>
        </Card>
      )}

      <Card
        title="Sending limits"
        footer={
          <span className="dim">
            These take effect within about ten seconds. No deploy, no restart.
          </span>
        }
      >
        {cfg && (
          <div className="col" style={{ gap: 14 }}>
            <div className="row w">
              <Pill tone={cfg.kill_switch ? "ba" : "ok"}>
                {cfg.kill_switch ? "sending is stopped" : "sending is on"}
              </Pill>
              {cfg.kill_switch && cfg.kill_switch_reason && (
                <span className="dim">{cfg.kill_switch_reason}</span>
              )}
              <div className="sp" />
              <button
                className={cfg.kill_switch ? "btn p" : "btn d"}
                onClick={() => {
                  if (cfg.kill_switch) return api.killSwitch(false).then(load);
                  const reason = prompt("Why are you stopping all sending?");
                  if (reason !== null) api.killSwitch(true, reason).then(load);
                }}
              >
                {cfg.kill_switch ? "Resume sending" : "Stop all sending"}
              </button>
            </div>

            {/* Deliberately its own switch. If the individually-written
                messages start reading wrong there is no reason to halt every
                campaign in the business over it: this one makes them fall
                back to their templates within a tick and stops nothing else.
                Which is also why turning it off is not a scary red button. */}
            <div className="row w">
              <Pill tone={cfg.drafting_enabled ? "ok" : "nu"}>
                {cfg.drafting_enabled
                  ? "customized messages are on"
                  : "everything sends its template"}
              </Pill>
              <span className="dim" style={{ fontSize: 11.5 }}>
                {cfg.drafting_enabled
                  ? "Only where a blast or a step asks for it. Everything else is unaffected."
                  : "Nothing is queued or delayed — each send uses the copy it was created with."}
              </span>
              <div className="sp" />
              <button
                className="btn"
                onClick={() =>
                  api
                    .updateSendConfig({ drafting_enabled: !cfg.drafting_enabled })
                    .then(load)
                    .catch((x) => setError(x.message))
                }
              >
                {cfg.drafting_enabled
                  ? "Send templates instead"
                  : "Turn customized messages back on"}
              </button>
            </div>

            {(
              [
                ["max_per_day", "Most texts one person gets per day"],
                ["max_per_7d", "Most per person per week"],
                ["min_gap_minutes", "Minimum gap between texts (minutes)"],
                ["daily_ceiling", "Total across everyone per day"],
              ] as const
            ).map(([key, label]) => (
              <div className="row w" key={key}>
                <span style={{ width: 300, fontSize: 12.5 }}>{label}</span>
                <input
                  className="f"
                  style={{ width: 110 }}
                  type="number"
                  defaultValue={cfg[key] as number}
                  onBlur={(e) =>
                    api
                      .updateSendConfig({ [key]: Number(e.target.value) })
                      .then(load)
                      .catch((x) => setError(x.message))
                  }
                />
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}
